import {action, computed, makeObservable, observable, runInAction} from "mobx";

import {Workspace} from "../API";
import {User} from "../utils/typescript/interfaces";
import {ContextMenuProps} from "../ui/elements/context-menu";
import {UserSessionState} from "./UserSessionState";
import {ModalController, ModalEntry} from "./ModalController";
import {MODALS} from "../ui/screens/modals/modal-names";
import {MobileNavController} from "../volatile/MobileNavController";

import {createRootStore} from "./createStore";

import {getSentry} from "../utils/errors/getSentry";
import {cacheManager} from "./cache/CacheManager";

import {inAppEventsHub} from "../volatile/InAppEventsHub";

// Platform service imports
import {ServiceRegistry} from "../platform/ServiceRegistry";
import {RouterService} from "../navigation/RouterService";
import {RightMenuName, RootStoreModel} from "@stores/RootStore";
import {DocumentTreeSource} from "../ui/elements/ListView/DocumentTreeSource";


export class AppState {
    rootStore: RootStoreModel;
    mobileNavController: MobileNavController;
    userState?: UserSessionState;
    contextMenu: ContextMenuProps | null = null;
    additionalContextMenus: ContextMenuProps[] = [];
    modalController = new ModalController();
    hasAuthUser: boolean = false;

    // Service registry for dependency injection (required)
    private serviceRegistry: ServiceRegistry;

    // Router service with injected dependencies
    public routerService: RouterService;

    constructor(serviceRegistry: ServiceRegistry) {
        this.serviceRegistry = serviceRegistry;
        this.rootStore = createRootStore({
            toggleIsTreeCollapsed: (newValue?: boolean) => this.rootStore.toggleIsTreeCollapsed(newValue),
            setRightMenu: (rightMenu: RightMenuName) => this.rootStore.setRightMenu(rightMenu),
            setModal: this.setModal,
            getUserState: () => this.userState,
            getDsManager: () => this.userState?.dsManager,
            routerService: () => this.routerService,
            getServiceRegistry: () => this.getServiceRegistry(),
        });
        this.routerService = new RouterService(this);
        this.mobileNavController = new MobileNavController(this.rootStore);

        makeObservable(this, {
            contextMenu: observable,
            userState: observable,
            additionalContextMenus: observable,
            modalController: observable,
            hasAuthUser: observable,
            setHasAuthUser: action,
            setContextMenu: action,
            addAdditionalContextMenu: action,
            removeAdditionalContextMenu: action,
            closeContextMenusByType: action,
            latestAdditionalCtxMenu: computed,
            currentWorkspaceDb: computed,
            ctxMenuMap: computed,
            tree: computed,
        });
    }

    setModal = (entry: ModalEntry | null) => {
        if (entry === null) {
            this.modalController.close();
        } else {
            this.modalController.open(entry);
        }
    }

    get topModal() {
        return this.modalController.top?.name ?? null;
    }

    getCurrentWsUsersDS() {
        const currentWsId = this.rootStore.view.workspace?.id;
        if (!currentWsId) {
            return null
        }
        return this.userState?.dsManager?.getWsUsersDataSource(currentWsId);
    }

    setHasAuthUser(hasAuthUser: boolean) {
        this.hasAuthUser = hasAuthUser;
    }

    resetState() {
        if (this.userState) {
            this.userState.destroy();
        }
        runInAction(() => {
            this.userState = undefined;
            this.contextMenu = null;
            this.additionalContextMenus = [];
        });
        this.rootStore.resetState();
        this.setHasAuthUser(false);

        // Attempt to clear cache on logout (graceful failure)
        this.clearCacheOnLogout();

        this.modalController.open({name: MODALS.LOGIN, params: undefined});
    }

    destroy() {
        if (this.userState) {
            this.userState.destroy();
        }
        runInAction(() => {
            this.userState = undefined;
        });
    }

    private async clearCacheOnLogout(): Promise<void> {
        try {
            await cacheManager.clear();
            console.log('Cache cleared on logout');
        } catch (error) {
            console.warn('Failed to clear cache on logout (will be handled on next login):', error);
            // Don't throw - graceful failure as specified in requirements
        }
    }

    get currentWorkspaceDb(): Workspace | undefined {
        const currentWsId = this.rootStore.view.workspace?.id;
        return this.userState?.dsManager?.workspacesDS.allRows.find(wsDb => wsDb.id === currentWsId);
    }

    get latestAdditionalCtxMenu(): ContextMenuProps | null {
        return this.additionalContextMenus.length ? this.additionalContextMenus[this.additionalContextMenus.length - 1] : null;
    }

    get ctxMenuMap() {
        const map = new Map();
        if (this.contextMenu) {
            map.set(`${this.contextMenu.type}#${this.contextMenu.id}`, this.contextMenu);
        }
        for (const addCtxMenu of this.additionalContextMenus) {
            map.set(`${addCtxMenu.type}#${addCtxMenu.id}`, addCtxMenu);
        }
        return map;
    }

    isCtxMenuOpen(type: string, id?: string) {
        return this.ctxMenuMap.has(`${type}#${id}`);
    }

    setContextMenu = (contextMenu: ContextMenuProps | null) => {
        if (this.contextMenu !== null
            && (
                (this.contextMenu.id === undefined)
                || this.contextMenu.id !== contextMenu?.id
            )
        ) {
            // there are elements on the app that calls setContextMenu(null) manually to close the context windows,
            // because they are stopping click propagation so the context menu can't close itself
            inAppEventsHub.dispatch({
                type: 'ContextMenuClose',
            });
            if (this.contextMenu.onClose !== undefined) {
                this.contextMenu.onClose();
            }
            if (this.additionalContextMenus.length > 0) {
                this.additionalContextMenus = [];
            }
        }
        this.contextMenu = contextMenu;
    }

    addAdditionalContextMenu = (contextMenu: ContextMenuProps, closeOthers?: boolean) => {
        if (closeOthers === undefined) {
            closeOthers = !this.mobileNavController.isMobile;
        }

        const incomingKey = `${contextMenu.type}#${contextMenu.id}`;

        // If base menu matches incoming (same type/id) → update base instead of stacking
        if (this.contextMenu && `${this.contextMenu.type}#${this.contextMenu.id}` === incomingKey) {
            this.setContextMenu(contextMenu);
            return;
        }

        // If an additional menu with the same key exists → replace it (re-render instead of stacking)
        const idx = this.additionalContextMenus.findIndex(m => `${m.type}#${m.id}` === incomingKey);
        if (idx >= 0) {
            const next = this.additionalContextMenus.slice();
            next[idx] = contextMenu;
            this.additionalContextMenus = next;
            return;
        }

        // Otherwise, add as new (or replace others if requested)
        if (this.contextMenu === null || closeOthers) {
            this.setContextMenu(contextMenu);
        } else {
            this.additionalContextMenus = [...this.additionalContextMenus, contextMenu];
        }
    }

    closeContextMenusByType = (type: ContextMenuProps['type'], id?: string): boolean => {
        const matches = (m: ContextMenuProps | null) =>
            !!m && m.type === type && (id === undefined || m.id === id);

        let closedSomething = false;
        let closedBase = false;


        if (matches(this.contextMenu)) {
            const prev = this.contextMenu!;
            this.contextMenu = null;
            closedBase = true;
            closedSomething = true;
            try {
                prev.onClose?.();
            } catch {
            }
        }

        if (this.additionalContextMenus.length) {
            const kept: ContextMenuProps[] = [];
            const removed: ContextMenuProps[] = [];
            for (const m of this.additionalContextMenus) {
                if (matches(m)) removed.push(m);
                else kept.push(m);
            }
            if (removed.length) {
                closedSomething = true;
                for (const m of removed) {
                    try {
                        m.onClose?.();
                    } catch {
                    }
                }
                this.additionalContextMenus = kept;
            }
        }

        if (closedBase && this.additionalContextMenus.length > 0) {
            this.additionalContextMenus = [];
        }

        if (closedSomething) {
            inAppEventsHub.dispatch({type: 'ContextMenuClose'});
        }

        return closedSomething;
    };

    removeAdditionalContextMenu = () => {
        this.additionalContextMenus = this.additionalContextMenus.slice(0, this.additionalContextMenus.length - 1);
    }

    async initUserState(user: User) {
        getSentry().then(sentryAsync => {
            sentryAsync.setSentryUser({
                id: user.sub,
                email: user.email,
            });
        });

        /* init connection with prosemirror-server */
        const authService = this.serviceRegistry.getAuthService();
        this.serviceRegistry.initStorageService(user["custom:clientId"]);
        const storageService = this.serviceRegistry.getStorageService();
        const state = new UserSessionState(
            this,
            storageService,
            user,
            () => this.rootStore.isOnline
        );

        if (this.userState) {
            this.userState.destroy();
        }

        runInAction(() => {
            this.userState = state;
        });

        await state.init();
        this.setHasAuthUser(true);

        return state;
    }

    /**
     * Get the service registry (for accessing platform services)
     */
    getServiceRegistry(): ServiceRegistry {
        return this.serviceRegistry;
    }

    get tree() {
        return new DocumentTreeSource({
            crudServices: {
                authService: this.serviceRegistry.getAuthService(),
                dataClientService: this.serviceRegistry.getDataClientService(),
            },
            appState: this,
        }, this.rootStore.view.workspace!);
    }
}
